import { t } from 'i18next'
import { useTranslation } from 'react-i18next'

import { IconCloseSm } from 'assets/svg/sprite'
import {
  DEFAULT_STORY_BACKGROUND,
  getStoryBackground
} from 'components/UI/BackgroundPicker/BackgroundPicker'
import { type IPoll } from 'types/interfaces/ITypes'
import './_pollPreview.scss'

type TypeFrom = 'new' | 'preview'

interface PollProps {
    title: string
    options: string[] | null
    from: TypeFrom
    onChange?: (value: string) => any
}

export const Poll = ({ title, options, from = 'new', onChange }: PollProps) => {
    const { t } = useTranslation()
    const keys = [t('a'), t('b'), t('c'), t('d')]

    
    return (
        <div className='poll'>
            <div className='poll__header'>
                <p>{title}</p>
            </div>
            <div className='poll__content'>
                {options?.map((option, key) => {
                    if (option) {
                        return (
                            <label
                                key={key}
                                htmlFor={option}
                                className={'poll__option'}
                            >
                                <input
                                    id={option}
                                    type='radio'
                                    name='poll_answers'
                                    value={option}
                                    disabled={from === 'new'}
                                    onChange={event =>
                                        !!onChange &&
                                        onChange(event.target.value)
                                    }
                                />
                                <span className='poll__option__icon'>
                                    {keys[key]}
                                </span>
                                <span className='poll__option__input'>
                                    {option}
                                </span>
                            </label>
                        )
                    }

                    return null
                })}
            </div>
        </div>
    )
}

interface AskQuestionProps {
    title: string
    from: TypeFrom
    onChange?: (value: string) => any
}

export const AskQuestion = ({
    title,
    from = 'new',
    onChange
}: AskQuestionProps) => {
    return (
        <div className='askQuestion'>
            <div className='askQuestion__header'>
                <p>{title}</p>
            </div>
            <div className='askQuestion__content'>
                <div className='askQuestion__option'>
                    <div className='askQuestion__option__input'>
                        <input
                            type='text'
                            name='questionText'
                            placeholder={t('typeSomething')}
                            maxLength={25}
                            disabled={from === 'new'}
                            onChange={event =>
                                !!onChange && onChange(event.target.value)
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

interface YesNoQuestionProps {
    title: string
    from: TypeFrom
    onChange?: (value: string) => any
}

export const YesNoQuestion = ({
    title,
    from = 'new',
    onChange
}: YesNoQuestionProps) => {
    return (
        <div className='yesNoQuestion'>
            <div className='yesNoQuestion__input'>{title}</div>
            <div className='yesNoQuestion__buttons'>
                <button
                    className='yesNoQuestion__button yesNoQuestion__button--yes'
                    disabled={from === 'new'}
                    onClick={() => !!onChange && onChange('yes')}
                >
                    {t('yes')}
                </button>
                <button
                    className='yesNoQuestion__button yesNoQuestion__button--no'
                    disabled={from === 'new'}
                    onClick={() => !!onChange && onChange('no')}
                >
                    {t('no')}
                </button>
            </div>
        </div>
    )
}

interface PollPreviewProps {
  pollData: IPoll
  from?: TypeFrom
  clearPollData?: () => void
  onChange?: () => any
  hideBackground?: boolean
}

export const PollPreview = ({
  pollData,
  from = 'new',
  clearPollData,
  onChange,
  hideBackground = false
}: PollPreviewProps) => {
  const isImageValue = (value: unknown) =>
    typeof value === 'string' &&
    (value.startsWith('http') ||
      value.startsWith('blob:') ||
      value.startsWith('data:image') ||
      value.startsWith('/'))

  const pollBgImage =
    isImageValue((pollData as any).pollBgUploadedSrc)
      ? (pollData as any).pollBgUploadedSrc
      : isImageValue(pollData.pollBgImg)
      ? pollData.pollBgImg
      : isImageValue(pollData.pollBg)
      ? pollData.pollBg
      : null

  const pollBgColor =
    !pollBgImage && pollData.pollBg
      ? pollData.pollBg
      : DEFAULT_STORY_BACKGROUND

  return (
    <div className='pollPreview'>
      <div
        className='pollPreview__wrapper'
        style={
          hideBackground
            ? { backgroundImage: 'none' }
            : {
                backgroundImage: pollBgImage
                  ? `url(${pollBgImage})`
                  : getStoryBackground(pollBgColor)
              }
        }
      >
        {from === 'new' && (
          <div className='pollPreview__close' onClick={clearPollData}>
            <IconCloseSm color='#0D1444' />
          </div>
        )}

        {pollData?.type === 'poll' && (
          <Poll
            title={pollData.question}
            options={pollData.answers}
            from={from}
            onChange={onChange}
          />
        )}

        {pollData?.type === 'ask' && (
          <AskQuestion
            title={pollData.question}
            from={from}
            onChange={onChange}
          />
        )}

        {pollData?.type === 'yes/no' && (
          <YesNoQuestion
            title={pollData.question}
            from={from}
            onChange={onChange}
          />
        )}
      </div>
    </div>
  )
}